import { BaseElement } from '../../base-element';
import { Signal } from 'signal-polyfill';
import { secretaryApi } from '../../../api/secretary.api';
import type { EntryDetailDto, EntryStatus, PdfStatus, ProcessingDto } from '../../../api/types';
import { navigate } from '../../../router';
import './components/processing-status-card';
import '../../ui/dt-card';
import '../../ui/dt-button';
import '../../ui/dt-badge';
import '../../ui/dt-spinner';

interface BadgeConfig {
  variant: 'success' | 'warning' | 'error' | 'info';
  label: string;
}

type PageState = 'loading' | 'loaded' | 'error';

type ProcessingCardElement = HTMLElement & {
  processing: ProcessingDto;
};

type DtButtonElement = HTMLElement & {
  disabled?: boolean;
  loading?: boolean;
};

export class SecretaryDetailPage extends BaseElement {
  private entryId = '';
  private trialEventId = '';
  private entry = new Signal.State<EntryDetailDto | null>(null);
  private loading = new Signal.State(false);
  private error = new Signal.State<string | null>(null);
  private isDownloading = new Signal.State(false);

  private pageState = new Signal.Computed<PageState>(() => {
    if (this.loading.get()) return 'loading';
    if (this.error.get()) return 'error';
    return 'loaded';
  });

  private readonly gridRows = ['Sheep', 'Cattle', 'Ducks', 'Mixed'];
  private readonly gridCols = [
    'STD',
    'OPN',
    'ADV',
    'FTD_OPN',
    'FTD_ADV',
    'NOV',
    'WRK_JR_HNDLR',
    'FEO',
    'POST_RTD_ADV',
  ];

  static get observedAttributes(): string[] {
    return ['entry-id', 'trial-event-id'];
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    if (name === 'entry-id') {
      this.entryId = newValue || '';
      if (this.isConnected && this.trialEventId) {
        void this.loadEntry();
      }
    } else if (name === 'trial-event-id') {
      this.trialEventId = newValue || '';
      if (this.isConnected && this.entryId) {
        void this.loadEntry();
      }
    }
  }

  connectedCallback(): void {
    this.watch(this.entry, () => this.updateRender());
    this.watch(this.loading, () => this.updateRender());
    this.watch(this.error, () => this.updateRender());
    this.watch(this.isDownloading, () => this.updateRender());
    this.updateRender();

    if (this.entryId && this.trialEventId) {
      void this.loadEntry();
    }
  }

  private async loadEntry(): Promise<void> {
    if (!this.entryId || !this.trialEventId) {
      this.error.set('Missing entry or trial event identifier.');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    const response = await secretaryApi.getEntryDetail(this.trialEventId, this.entryId);
    if (response.error || !response.data) {
      this.entry.set(null);
      this.error.set('Unable to load entry details.');
      this.loading.set(false);
      return;
    }

    this.entry.set(response.data);
    this.loading.set(false);
  }

  private formatDate(value?: string | null, withTime = false): string {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
    });
  }

  private formatAddress(address?: {
    street?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  } | null): string {
    if (!address) return '—';
    const parts = [address.street, address.city, address.state, address.zip].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '—';
  }

  private getStatusBadge(status: EntryStatus): BadgeConfig {
    if (status === 'Submitted') {
      return { variant: 'success', label: 'Submitted' };
    }
    return { variant: 'info', label: 'Draft' };
  }

  private getPdfBadge(status: PdfStatus): BadgeConfig {
    switch (status) {
      case 'Success':
        return { variant: 'success', label: 'Ready' };
      case 'Failed':
        return { variant: 'error', label: 'Failed' };
      case 'InProgress':
        return { variant: 'warning', label: 'Processing' };
      case 'Queued':
      default:
        return { variant: 'info', label: 'Queued' };
    }
  }

  private hasSelection(items: { row: string; col: string }[], row: string, col: string): boolean {
    return items.some((selection) => selection.row === row && selection.col === col);
  }

  private formatCol(col: string): string {
    return col.replace(/_/g, ' ');
  }

  private handleBack = (): void => {
    navigate('/secretary');
  };

  private async downloadPdf(): Promise<void> {
    const entry = this.entry.get();
    if (!entry || this.isDownloading.get() || !this.trialEventId) return;

    this.isDownloading.set(true);
    const response = await secretaryApi.getPdfDownloadUrl(this.trialEventId, entry.entryId);
    this.isDownloading.set(false);

    if (response.data?.downloadUrl) {
      window.open(response.data.downloadUrl, '_blank');
    }
  }

  private async retryPdf(): Promise<void> {
    const entry = this.entry.get();
    if (!entry || !this.trialEventId) return;

    const response = await secretaryApi.retryPdf(this.trialEventId, entry.entryId);
    if (response.error) return;

    this.entry.set({
      ...entry,
      processing: {
        ...entry.processing,
        pdfStatus: 'Queued',
      },
    });
  }

  private renderLoading(): string {
    return `
      <div class="flex min-h-[240px] flex-col items-center justify-center gap-3" data-testid="loading-state">
        <dt-spinner size="large"></dt-spinner>
        <p class="text-sm text-neutral-600">Loading entry...</p>
      </div>
    `;
  }

  private renderError(message: string): string {
    return `
      <div class="flex min-h-[240px] flex-col items-center justify-center gap-4" data-testid="error-state">
        <div class="rounded-2xl border border-error-200 bg-error-50 p-6 text-center">
          <h2 class="text-lg font-semibold text-error-700">Unable to load entry</h2>
          <p class="mt-2 text-sm text-error-600">${message}</p>
        </div>
      </div>
    `;
  }

  private renderDetail(entry: EntryDetailDto): string {
    const statusBadge = this.getStatusBadge(entry.status);
    const pdfBadge = this.getPdfBadge(entry.processing.pdfStatus as PdfStatus);

    return `
      <div class="flex flex-col gap-6" data-testid="detail-content">
        <dt-card variant="outlined">
          <div slot="header" class="flex flex-wrap items-center justify-between gap-3">
            <span>Entry Detail</span>
            <dt-badge variant="${statusBadge.variant}">${statusBadge.label}</dt-badge>
          </div>
          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Entry ID</div>
              <div class="font-mono text-xs text-neutral-700" data-testid="entry-id">${entry.entryId}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Entry #</div>
              <div class="text-sm font-semibold text-neutral-800" data-testid="entry-number">${entry.entryNumber ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Submitted</div>
              <div class="text-sm text-neutral-700" data-testid="entry-submitted">${this.formatDate(entry.submittedAtUtc, true)}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Trial</div>
              <div class="text-sm text-neutral-700" data-testid="entry-trial">${entry.trialEvent?.name ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Handler Email</div>
              <div class="text-sm text-neutral-700">${entry.createdForUserEmail}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">PDF Status</div>
              <dt-badge variant="${pdfBadge.variant}">${pdfBadge.label}</dt-badge>
            </div>
          </div>
        </dt-card>

        <dt-card variant="outlined">
          <div slot="header">Dog Information</div>
          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Call Name</div>
              <div class="text-sm font-semibold text-neutral-800" data-testid="dog-call">${entry.dog.callName ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Registered Name</div>
              <div class="text-sm text-neutral-700">${entry.dog.registeredName ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">ASCA Reg #</div>
              <div class="text-sm text-neutral-700">${entry.dog.ascaRegistrationNumber ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Breed</div>
              <div class="text-sm text-neutral-700">${entry.dog.breed ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">DOB</div>
              <div class="text-sm text-neutral-700">${entry.dog.dob ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Sex</div>
              <div class="text-sm text-neutral-700">${entry.dog.sex ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Color</div>
              <div class="text-sm text-neutral-700">${entry.dog.color ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Sire</div>
              <div class="text-sm text-neutral-700">${entry.dog.sire ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Dam</div>
              <div class="text-sm text-neutral-700">${entry.dog.dam ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Breeders</div>
              <div class="text-sm text-neutral-700">${entry.dog.breeders ?? '—'}</div>
            </div>
          </div>
        </dt-card>

        <dt-card variant="outlined">
          <div slot="header">Contact / Emergency / Fees</div>
          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Owners</div>
              <div class="text-sm text-neutral-700">${entry.contact.owners ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Email</div>
              <div class="text-sm text-neutral-700">${entry.createdForUserEmail ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Phone</div>
              <div class="text-sm text-neutral-700">${entry.contact.phone ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Handler</div>
              <div class="text-sm text-neutral-700">${entry.contact.handler ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Membership #</div>
              <div class="text-sm text-neutral-700">${entry.contact.membershipNumber ?? '—'}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Owner Address</div>
              <div class="text-sm text-neutral-700">${this.formatAddress(entry.contact.ownerAddress)}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Junior</div>
              <div class="text-sm text-neutral-700">${entry.contact.junior?.memberId ?? '—'}${entry.contact.junior?.dob ? ` (${entry.contact.junior?.dob})` : ''}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Emergency Contact</div>
              <div class="text-sm text-neutral-700">${entry.emergencyContact.name ?? '—'}${entry.emergencyContact.phoneOrNumber ? ` (${entry.emergencyContact.phoneOrNumber})` : ''}</div>
            </div>
            <div>
              <div class="text-xs font-semibold uppercase text-neutral-500">Total Fees</div>
              <div class="text-sm text-neutral-700">${entry.fees.totalEntryFees ?? '—'} ${entry.fees.currency ?? ''}</div>
            </div>
          </div>
        </dt-card>

        <dt-card variant="outlined">
          <div slot="header">Class Selections</div>
          <div class="overflow-x-auto">
            <table class="min-w-full border border-neutral-200 text-xs" data-testid="selection-grid">
              <thead>
                <tr class="bg-neutral-50 text-neutral-600">
                  <th class="border border-neutral-200 px-2 py-2 text-left"></th>
                  ${this.gridCols.map((col) => `<th class="border border-neutral-200 px-2 py-2 text-left">${this.formatCol(col)}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${this.gridRows.map((row) => `
                  <tr>
                    <th class="border border-neutral-200 px-2 py-2 text-left font-semibold text-neutral-700">${row}</th>
                    ${this.gridCols.map((col) => `
                      <td class="border border-neutral-200 px-2 py-2 text-center">${this.hasSelection(entry.selections.items, row, col) ? 'X' : ''}</td>
                    `).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </dt-card>

        <div class="flex flex-wrap items-center gap-3">
          <dt-button
            variant="primary"
            data-testid="download-pdf"
          >Download PDF</dt-button>
          ${entry.processing.pdfStatus === 'Failed'
            ? '<dt-button variant="outline" data-testid="retry-pdf">Retry PDF</dt-button>'
            : ''}
        </div>

        <dt-processing-status-card data-testid="processing-status"></dt-processing-status-card>
      </div>
    `;
  }

  private updateRender(): void {
    const pageState = this.pageState.get();
    let content = '';

    if (pageState === 'loading') {
      content = this.renderLoading();
    } else if (pageState === 'error') {
      content = this.renderError(this.error.get() ?? 'Unable to load.');
    } else {
      const entry = this.entry.get();
      if (entry) {
        content = this.renderDetail(entry);
      }
    }

    this.render(`
      <section class="px-4 py-6" data-testid="secretary-detail-page">
        <div class="mx-auto flex w-full max-w-5xl flex-col gap-6">
          <dt-button variant="ghost" data-testid="back-btn">Back to list</dt-button>
          ${content}
        </div>
      </section>
    `);

    this.attachEventListeners();
    this.assignData();
  }

  private attachEventListeners(): void {
    const backBtn = this.$('[data-testid="back-btn"]');
    if (backBtn) {
      backBtn.addEventListener('click', this.handleBack);
    }

    const downloadBtn = this.$('[data-testid="download-pdf"]');
    if (downloadBtn) {
      downloadBtn.addEventListener('click', () => void this.downloadPdf());
    }

    const retryBtn = this.$('[data-testid="retry-pdf"]');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => void this.retryPdf());
    }
  }

  private assignData(): void {
    const entry = this.entry.get();
    if (!entry) return;

    const processingCard = this.$('dt-processing-status-card') as ProcessingCardElement | null;
    if (processingCard) {
      processingCard.processing = entry.processing;
    }

    const downloadBtn = this.$('[data-testid="download-pdf"]') as DtButtonElement | null;
    if (downloadBtn) {
      downloadBtn.disabled = entry.processing.pdfStatus !== 'Success' || this.isDownloading.get();
      downloadBtn.loading = this.isDownloading.get();
    }
  }
}

customElements.define('dt-secretary-detail-page', SecretaryDetailPage);
